import React, { useEffect, useState, useCallback, useMemo } from 'react';
import css from './AlgoliaFilter.module.css';
import { Button, IconCollection, Modal, RangeSlider } from '../../../../components';
import { FormattedMessage } from 'react-intl';

// Constants
const DEFAULT_PRICE_RANGE = [0, 10000];
const PRICE_STEP = 50;
const FILTER_SECTIONS = {
    category: 'AlgoliaFilters.section.category',
    Condition_of_the_item: 'AlgoliaFilters.section.conditionOfTheItem',
    Trust_and_verification: 'AlgoliaFilters.section.trustAndVerification',
    access_to_rentals: 'AlgoliaFilters.section.accessToRentals',
    cancelation_policy: 'AlgoliaFilters.section.cancellationPolicy',
    security_deposit: 'AlgoliaFilters.section.securityDeposit',
    reason_for_listing: 'AlgoliaFilters.section.reasonForListing'
};

/**
 * Sort section component — renders sort options as expanded buttons inside the filter modal
 */
const SortSection = React.memo(({ sortOptions, selectedSort, onSelect }) => {
    if (!sortOptions || sortOptions.length === 0) return null;
    return (
        <div className={css.section}>
            <h3 className={css.sectionTitle}>
                <FormattedMessage id="SortBy.heading" defaultMessage="Sort By" />
            </h3>
            <div className={css.filterOptions}>
                {sortOptions.map(option => (
                    <button
                        key={option.key}
                        className={`${css.category} ${selectedSort === option.key ? css.categorySelected : ''}`}
                        onClick={() => { if (selectedSort !== option.key) onSelect(option.key); }}
                    >
                        {option.label}
                    </button>
                ))}
            </div>
        </div>
    );
});

/**
 * Filter section component that renders a group of filter options
 */
const FilterSection = React.memo(({ title, items, selectedValue, onSelect, onFilterClick }) => {
    if (!items || items.length === 0) return null;
    return (
        <div className={css.section}>
            <h3 className={css.sectionTitle}>
                <FormattedMessage id={title} defaultMessage={title} />
            </h3>
            <div className={css.filterOptions}>
                {items.map(item => (
                    <button
                        key={item.value}
                        className={`${css.category} ${selectedValue === item.value ? css.categorySelected : ''}`}
                        onClick={() => {
                            if (selectedValue === item.value) return;
                            onSelect(item.value);
                            if (onFilterClick) onFilterClick(item.value);
                        }}
                    >
                        <FormattedMessage
                            id={`enum.${item.value}.label`}
                            defaultMessage={item.label}
                        />
                    </button>
                ))}
            </div>
        </div>
    );
});

/**
 * Price range filter component
 */
export const PriceRangeFilter = React.memo(({ availablePriceRange, priceRange, setPriceRange }) => {
    // Ensure price range values are properly ordered and within valid range
    const validPriceRange = [
        Math.max(availablePriceRange[0], Math.min(priceRange[0], availablePriceRange[1])),
        Math.max(availablePriceRange[0], Math.min(priceRange[1], availablePriceRange[1]))
    ];

    // Ensure min is not greater than max
    if (validPriceRange[0] > validPriceRange[1]) {
        validPriceRange[0] = validPriceRange[1];
    }

    return (
        <div className={css.section}>
            <h3 className={css.sectionTitle}>
                <FormattedMessage id="AlgoliaFilters.section.priceRange" defaultMessage="Price Range" />
            </h3>
            <div className={css.priceRange}>
                <RangeSlider
                    min={availablePriceRange[0]}
                    max={availablePriceRange[1]}
                    step={PRICE_STEP}
                    handles={validPriceRange}
                    onChange={setPriceRange}
                />
                <div className={css.priceLabels}>
                    <span><FormattedMessage id="AlgoliaFilters.section.priceMin"  /></span>
                    <span><FormattedMessage id="AlgoliaFilters.section.priceMax" /></span>
                </div>
                <div className={css.priceLabels}>
                    <span className={css.inputBox}>${validPriceRange[0]}</span>
                    <span className={css.inputBox}>${validPriceRange[1]}</span>
                </div>
            </div>
        </div>
    )
});

/**
 * Main AlgoliaFilters component
 */
function AlgoliaFilters(props) {
    const { facets, manageDisableScrolling, setIsOpen, isOpen, handleShowRentals, selectedFilters, showResetButton, onResetFilters, onFilterClick, sortOptions, currentSort } = props;

    // State
    // The available price range is the price range from the facets
    const availablePriceRange = useMemo(() => {
        const priceFacet = facets?.['price.amount'];
        const min = (+priceFacet?.[0]?.value / 100) || 0;
        const max = (+priceFacet?.[priceFacet?.length - 1]?.value / 100) || 10000;
        return [min, max];
    }, [facets]);

    // The default price range is the price range from the selected filters
    const defaultPriceRange = useMemo(() => {
        if (selectedFilters?.['priceRange']) {
            const [min, max] = selectedFilters['priceRange'];
            // Ensure values are within available range and properly ordered
            return [
                Math.max(availablePriceRange[0], Math.min(min, availablePriceRange[1])),
                Math.max(availablePriceRange[0], Math.min(max, availablePriceRange[1]))
            ];
        }
        return availablePriceRange;
    }, [selectedFilters, availablePriceRange]);

    const [localFilters, setLocalFilters] = useState({});
    const [priceRange, setPriceRange] = useState(defaultPriceRange);
    const [localSort, setLocalSort] = useState(currentSort || null);

    // Initialize local filters and sort when modal opens
    useEffect(() => {
        if (isOpen) {
            setLocalFilters(selectedFilters);
            setPriceRange(defaultPriceRange);
            setLocalSort(currentSort || null);
        }
    }, [isOpen, selectedFilters, defaultPriceRange, currentSort]);

    // Handle filter selection
    const handleFilterSelect = useCallback((filterKey, value) => {
        setLocalFilters(prev => ({
            ...prev,
            [filterKey]: value,
        }));
    }, []);

    // Handle clear all filters
    const handleClearAll = useCallback(() => {
        setLocalFilters({});
        setPriceRange(availablePriceRange);
    }, [availablePriceRange]);

    // Handle apply filters (includes sort when sort options are provided)
    const handleApplyFilters = useCallback(() => {
        const filters = { ...localFilters, priceRange };
        if (sortOptions && sortOptions.length > 0) {
            filters.sort = localSort;
        }
        handleShowRentals(filters);
    }, [localFilters, priceRange, localSort, sortOptions, handleShowRentals]);

    // Handle modal close
    const handleClose = useCallback(() => {
        setIsOpen(false);
    }, [setIsOpen]);

    // Manage body scroll and z-index when modal is open
    useEffect(() => {
        const header = document.querySelector('header');
        const mobileBottomNavbar = document.querySelector('.mobileBottomNavbar');

        if (isOpen) {
            document.body.style.overflow = 'hidden';
            if (header) header.style.zIndex = '0';
            if (mobileBottomNavbar) mobileBottomNavbar.style.zIndex = '0';
        } else {
            document.body.style.overflow = '';
            if (header) header.style.zIndex = '';
            if (mobileBottomNavbar) mobileBottomNavbar.style.zIndex = '';
        }

        return () => {
            document.body.style.overflow = '';
            if (header) header.style.zIndex = '';
            if (mobileBottomNavbar) mobileBottomNavbar.style.zIndex = '';
        };
    }, [isOpen]);

    // Memoize filter sections to prevent unnecessary re-renders
    const filterSections = useMemo(() =>
        Object.entries(FILTER_SECTIONS).map(([key, title]) => (
            <FilterSection
                key={key}
                title={title}
                items={facets?.[key]}
                selectedValue={localFilters[key]}
                onSelect={(value) => handleFilterSelect(key, value)}
                onFilterClick={(value) => onFilterClick && onFilterClick(key, value)}
            />
        )),
        [facets, localFilters, handleFilterSelect, onFilterClick]
    );

    const handlePriceRangeChange = useCallback((value) => {
        // Ensure values are within available range and properly ordered
        const validPriceRange = [
            Math.max(availablePriceRange[0], Math.min(value[0], availablePriceRange[1])),
            Math.max(availablePriceRange[0], Math.min(value[1], availablePriceRange[1]))
        ];

        // Ensure min is not greater than max
        if (validPriceRange[0] > validPriceRange[1]) {
            validPriceRange[0] = validPriceRange[1];
        }

        setPriceRange(validPriceRange);
    }, [availablePriceRange]);

    return (
        <div className={css.algoliaFilterWrapper}>
            {showResetButton ? (
                <button className={css.resetFiltersButton} onClick={onResetFilters}>
                    <IconCollection name="reset_cross" /> Remove Filters
                </button>
            ) : (
                <button
                    className={css.moreFiltersLabel}
                    type="button"
                    onClick={() => setIsOpen(true)}
                >
                    <IconCollection name="filter_icon" />
                    <FormattedMessage id="AlgoliaFilters.filters" defaultMessage="Filters" />
                </button>
            )}

            <Modal
                className={css.moreFiltersModal}
                isOpen={isOpen}
                id="SearchPage"
                onManageDisableScrolling={manageDisableScrolling}
                onClose={handleClose}
            >
                <div className={css.filterModal}>
                    <div className={css.modalHeader}>
                        <h2 className={css.modalTitle}>
                            <FormattedMessage id="AlgoliaFilters.filters" defaultMessage="Filters" />
                        </h2>
                    </div>

                    <div className={css.modalBody}>
                        <SortSection
                            sortOptions={sortOptions}
                            selectedSort={localSort}
                            onSelect={setLocalSort}
                        />
                        {filterSections}
                        <PriceRangeFilter availablePriceRange={availablePriceRange} priceRange={priceRange} setPriceRange={handlePriceRangeChange} />
                    </div>

                    <div className={css.footer}>
                        <button
                            type="button"
                            className={css.clearBtn}
                            onClick={handleClearAll}
                        >
                            <FormattedMessage id="AlgoliaFilters.clearAll" defaultMessage="Clear All" />
                        </button>
                        <Button
                            type="button"
                            className={css.showBtn}
                            onClick={handleApplyFilters}
                        >
                            <FormattedMessage id="AlgoliaFilters.showRentals" defaultMessage="Show Rentals" />
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export default React.memo(AlgoliaFilters);
